/**
 * Version History Component
 * 
 * Displays timeline of document versions with metadata.
 * Two tabs: formal revisions (IR, A, B...) and S3 auto-saved versions within the current draft.
 * Allows comparing versions, viewing details, and restoring previous versions.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { documentsApi, documentVersionApi } from '../../services/api';
import type { S3VersionInfo } from '../../services/api';
import { useConfirm, Button, Badge, Spinner } from '@/components/ui';
import { logger } from '../../utils/logger';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DocumentVersion {
  revision: string;
  revisionDate: string;
  author: string;
  approvedBy?: string;
  approvalDate?: string;
  changeDescription: string;
  status: string;
  s3Key?: string;
  s3JsonKey?: string;
}

interface VersionHistoryProps {
  projectId: string;
  documentNumber: string;
  currentRevision: string;
  onCompare: (version1: string, version2: string) => void;
  onRestore: (revision: string) => void;
  onClose: () => void;
}

type VersionTab = 'revisions' | 'auto-saves';

interface AutoSaveGroup {
  id: string;
  startTime: Date;
  endTime: Date;
  versions: S3VersionInfo[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Group contiguous auto-save versions that fall within a 5-minute window. */
const AUTO_SAVE_GROUP_WINDOW_MS = 5 * 60 * 1000;

function groupVersionsByTimeWindow(versions: S3VersionInfo[]): AutoSaveGroup[] {
  if (versions.length === 0) return [];

  // Sort newest-first so groups read top-to-bottom chronologically descending
  const sorted = [...versions].sort(
    (a, b) => new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime(),
  );

  const groups: AutoSaveGroup[] = [];
  let currentGroup: AutoSaveGroup | null = null;

  for (const version of sorted) {
    const versionTime = new Date(version.lastModified);

    if (!currentGroup) {
      currentGroup = {
        id: `group-${version.versionId}`,
        startTime: versionTime,
        endTime: versionTime,
        versions: [version],
      };
      groups.push(currentGroup);
    } else {
      // Compare against the oldest version already in the current group
      const lastInGroup = currentGroup.versions[currentGroup.versions.length - 1];
      const gap = new Date(lastInGroup.lastModified).getTime() - versionTime.getTime();

      if (gap > AUTO_SAVE_GROUP_WINDOW_MS) {
        currentGroup = {
          id: `group-${version.versionId}`,
          startTime: versionTime,
          endTime: versionTime,
          versions: [version],
        };
        groups.push(currentGroup);
      } else {
        currentGroup.versions.push(version);
        currentGroup.startTime = versionTime;
      }
    }
  }

  return groups;
}

function formatTimeRange(start: Date, end: Date): string {
  const dateOpts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  const timeOpts: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' };

  const dateStr = start.toLocaleDateString('en-US', dateOpts);
  const startTime = start.toLocaleTimeString('en-US', timeOpts);
  const endTime = end.toLocaleTimeString('en-US', timeOpts);

  if (start.getTime() === end.getTime()) {
    return `${dateStr} at ${startTime}`;
  }
  return `${dateStr}, ${startTime} – ${endTime}`;
}

function formatTimestamp(date: Date): string {
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function VersionHistory({
  projectId,
  documentNumber,
  currentRevision,
  onCompare,
  onRestore,
  onClose,
}: VersionHistoryProps) {
  // ---- Shared state ----
  const [activeTab, setActiveTab] = useState<VersionTab>('revisions');
  const confirmDialog = useConfirm();

  // ---- Revisions tab state ----
  const [versions, setVersions] = useState<DocumentVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedVersions, setSelectedVersions] = useState<string[]>([]);
  const [expandedVersion, setExpandedVersion] = useState<string | null>(null);

  // ---- Auto-saves tab state ----
  const [autoSaveVersions, setAutoSaveVersions] = useState<S3VersionInfo[]>([]);
  const [autoSaveLoading, setAutoSaveLoading] = useState(false);
  const [autoSaveError, setAutoSaveError] = useState<string | null>(null);
  const [expandedGroup, setExpandedGroup] = useState<string | null>(null);
  const [restoringVersionId, setRestoringVersionId] = useState<string | null>(null);

  // ---- Revisions data fetching ----

  const loadVersionHistory = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await documentsApi.get(projectId, documentNumber, { includeContent: 'false' });

      if (response.revisions && response.revisions.length > 0) {
        const sorted = [...response.revisions].sort(
          (a, b) => new Date(b.revisionDate).getTime() - new Date(a.revisionDate).getTime(),
        );
        setVersions(sorted);
      } else {
        setVersions([]);
      }
    } catch (err: unknown) {
      logger.error('[VersionHistory] Failed to load versions:', err);
      setError(err instanceof Error ? err.message : 'Failed to load version history');
    } finally {
      setLoading(false);
    }
  }, [projectId, documentNumber]);

  useEffect(() => {
    loadVersionHistory();
  }, [loadVersionHistory]);

  // ---- Auto-saves data fetching ----

  const loadAutoSaveVersions = useCallback(async () => {
    setAutoSaveLoading(true);
    setAutoSaveError(null);

    try {
      const response = await documentVersionApi.listVersions(projectId, documentNumber);
      setAutoSaveVersions(response.versions);
    } catch (err: unknown) {
      logger.error('[VersionHistory] Failed to load auto-save versions:', err);
      setAutoSaveError(err instanceof Error ? err.message : 'Failed to load auto-save versions');
    } finally {
      setAutoSaveLoading(false);
    }
  }, [projectId, documentNumber]);

  useEffect(() => {
    if (activeTab === 'auto-saves') {
      loadAutoSaveVersions();
    }
  }, [activeTab, loadAutoSaveVersions]);

  /** Split auto-save versions into time-window groups + a pinned initial draft. */
  const autoSaveGroups = useMemo(() => {
    // Exclude the current (latest) version – the user is already looking at it
    const historical = autoSaveVersions.filter((v) => !v.isLatest);
    if (historical.length === 0) return { groups: [], initialDraft: null };

    // Sort oldest-first to peel off the initial draft
    const sorted = [...historical].sort(
      (a, b) => new Date(a.lastModified).getTime() - new Date(b.lastModified).getTime(),
    );
    const initialDraft = sorted[0];
    const remaining = sorted.slice(1);

    return {
      groups: groupVersionsByTimeWindow(remaining),
      initialDraft,
    };
  }, [autoSaveVersions]);

  // ---- Revisions tab handlers ----

  const handleVersionSelect = (revision: string) => {
    if (selectedVersions.includes(revision)) {
      setSelectedVersions((prev) => prev.filter((v) => v !== revision));
    } else if (selectedVersions.length < 2) {
      setSelectedVersions((prev) => [...prev, revision]);
    } else {
      setSelectedVersions([selectedVersions[0], revision]);
    }
  };

  const handleCompare = () => {
    if (selectedVersions.length === 2) {
      onCompare(selectedVersions[0], selectedVersions[1]);
    }
  };

  const handleRestore = async (revision: string) => {
    const confirmed = await confirmDialog({
      title: 'Restore Version',
      description: `Restore document to revision ${revision}? This will create a new draft based on this version.`,
      confirmText: 'Restore',
    });
    if (confirmed) {
      onRestore(revision);
    }
  };

  // ---- Auto-saves tab handlers ----

  const handleAutoSaveRestore = async (versionId: string) => {
    const confirmed = await confirmDialog({
      title: 'Restore Auto-Save',
      description: 'Restore the document to this auto-saved version? The current content will be replaced.',
      confirmText: 'Restore',
    });
    if (!confirmed) return;

    setRestoringVersionId(versionId);
    try {
      await documentVersionApi.restoreVersion(projectId, documentNumber, versionId);
      window.location.reload();
    } catch (err: unknown) {
      logger.error('[VersionHistory] Failed to restore auto-save version:', err);
      setAutoSaveError(err instanceof Error ? err.message : 'Failed to restore version');
    } finally {
      setRestoringVersionId(null);
    }
  };

  // ---- Helpers ----

  const getStatusBadgeVariant = (status: string): 'success' | 'warning' | 'info' | 'secondary' => {
    switch (status) {
      case 'Released':
        return 'success';
      case 'Draft':
        return 'warning';
      case 'In Review':
        return 'info';
      case 'Superseded':
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  // ---- Render ----

  return (
    <div className="flex flex-col h-full bg-card border-l border-border">
      {/* ---- Header ---- */}
      <div className="flex-shrink-0 border-b border-border bg-muted px-4 py-3">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-foreground">Version History</h3>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground text-lg leading-none"
            title="Close version history"
          >
            ×
          </button>
        </div>

        {/* Tab buttons */}
        <div className="flex gap-1 mt-1">
          <Button
            variant={activeTab === 'revisions' ? 'default' : 'secondary'}
            size="sm"
            onClick={() => setActiveTab('revisions')}
            className="text-xs"
          >
            Revisions
          </Button>
          <Button
            variant={activeTab === 'auto-saves' ? 'default' : 'secondary'}
            size="sm"
            onClick={() => setActiveTab('auto-saves')}
            className="text-xs"
          >
            Auto-Saves
          </Button>
        </div>

        {/* Revisions tab sub-header */}
        {activeTab === 'revisions' && !loading && !error && (
          <>
            <div className="text-xs text-muted-foreground mt-2">
              {versions.length} {versions.length === 1 ? 'version' : 'versions'}
            </div>

            {selectedVersions.length === 2 && (
              <Button size="sm" onClick={handleCompare} className="w-full mt-2">
                Compare Selected
              </Button>
            )}
          </>
        )}
      </div>

      {/* ---- Content ---- */}
      <div className="flex-1 overflow-y-auto">
        {activeTab === 'revisions' ? (
          <RevisionsContent
            loading={loading}
            error={error}
            versions={versions}
            currentRevision={currentRevision}
            selectedVersions={selectedVersions}
            expandedVersion={expandedVersion}
            projectId={projectId}
            documentNumber={documentNumber}
            onRetry={loadVersionHistory}
            onToggleExpand={(rev) => setExpandedVersion(expandedVersion === rev ? null : rev)}
            onSelect={handleVersionSelect}
            onRestore={handleRestore}
            getStatusBadgeVariant={getStatusBadgeVariant}
          />
        ) : (
          <AutoSavesContent
            loading={autoSaveLoading}
            error={autoSaveError}
            groups={autoSaveGroups.groups}
            initialDraft={autoSaveGroups.initialDraft}
            expandedGroup={expandedGroup}
            restoringVersionId={restoringVersionId}
            onRetry={loadAutoSaveVersions}
            onToggleGroup={(id) => setExpandedGroup(expandedGroup === id ? null : id)}
            onRestore={handleAutoSaveRestore}
          />
        )}
      </div>

      {/* ---- Footer (revisions tab only) ---- */}
      {activeTab === 'revisions' && (
        <div className="flex-shrink-0 border-t border-border bg-muted px-4 py-2">
          <div className="text-xs text-muted-foreground">
            {selectedVersions.length === 0
              ? 'Select up to 2 versions to compare'
              : selectedVersions.length === 1
                ? 'Select one more version to compare'
                : `Comparing Rev ${selectedVersions[0]} and Rev ${selectedVersions[1]}`}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Revisions sub-component (extracted for readability, not a separate file)
// ---------------------------------------------------------------------------

interface RevisionsContentProps {
  loading: boolean;
  error: string | null;
  versions: DocumentVersion[];
  currentRevision: string;
  selectedVersions: string[];
  expandedVersion: string | null;
  projectId: string;
  documentNumber: string;
  onRetry: () => void;
  onToggleExpand: (revision: string) => void;
  onSelect: (revision: string) => void;
  onRestore: (revision: string) => void;
  getStatusBadgeVariant: (status: string) => 'success' | 'warning' | 'info' | 'secondary';
}

function RevisionsContent({
  loading,
  error,
  versions,
  currentRevision,
  selectedVersions,
  expandedVersion,
  projectId,
  documentNumber,
  onRetry,
  onToggleExpand,
  onSelect,
  onRestore,
  getStatusBadgeVariant,
}: RevisionsContentProps) {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-32 gap-2">
        <Spinner size="sm" />
        <span className="text-sm text-muted-foreground">Loading version history...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="text-sm text-destructive mb-2">{error}</div>
        <Button variant="secondary" size="sm" onClick={onRetry}>
          Retry
        </Button>
      </div>
    );
  }

  if (versions.length === 0) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        <p className="text-sm">No version history available</p>
      </div>
    );
  }

  return (
    <div className="relative">
      {/* Timeline line */}
      <div className="absolute left-8 top-0 bottom-0 w-px bg-secondary" />

      {versions.map((version) => {
        const isExpanded = expandedVersion === version.revision;
        const isSelected = selectedVersions.includes(version.revision);
        const isCurrent = version.revision === currentRevision;

        return (
          <div
            key={version.revision}
            className={`relative pl-16 pr-4 py-4 border-b border-border hover:bg-muted transition-colors ${
              isSelected ? 'bg-info-light' : ''
            }`}
          >
            {/* Timeline dot */}
            <div
              className={`absolute left-6 top-6 w-4 h-4 rounded-full border-2 ${
                isCurrent
                  ? 'bg-success-light0 border-success'
                  : version.status === 'Released'
                    ? 'bg-info border-primary dark:border-primary'
                    : 'bg-card border-border'
              }`}
            />

            {/* Version card */}
            <div className="cursor-pointer" onClick={() => onToggleExpand(version.revision)}>
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-bold text-foreground">Rev {version.revision}</span>
                    <Badge variant={getStatusBadgeVariant(version.status)} className="text-xs">
                      {version.status}
                    </Badge>
                    {isCurrent && (
                      <Badge variant="success" className="text-xs">
                        Current
                      </Badge>
                    )}
                  </div>

                  <div className="text-xs text-muted-foreground">
                    {new Date(version.revisionDate).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </div>
                </div>

                {/* Selection checkbox */}
                {/* Raw checkbox - no UI component available (see style guide) */}
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={(e) => {
                    e.stopPropagation();
                    onSelect(version.revision);
                  }}
                  className="w-4 h-4 text-link rounded"
                  title="Select for comparison"
                />
              </div>

              <div className="text-xs text-foreground mb-1">
                <strong>By:</strong> {version.author}
              </div>

              <div className="text-xs text-muted-foreground">{version.changeDescription}</div>

              {/* Expanded details */}
              {isExpanded && (
                <div className="mt-3 pt-3 border-t border-border space-y-2">
                  {version.approvedBy && (
                    <div className="text-xs">
                      <strong className="text-foreground">Approved by:</strong>{' '}
                      <span className="text-muted-foreground">{version.approvedBy}</span>
                      {version.approvalDate && (
                        <span className="text-muted-foreground">
                          {' '}
                          on {new Date(version.approvalDate).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  )}

                  {/* Action buttons */}
                  <div className="flex gap-2 mt-3">
                    {version.revision !== currentRevision && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRestore(version.revision);
                        }}
                        className="text-xs"
                      >
                        Restore This Version
                      </Button>
                    )}

                    {version.s3Key && (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            const downloadUrl = await documentsApi.getDownloadUrl(
                              projectId,
                              documentNumber,
                              version.revision,
                            );
                            window.open(downloadUrl, '_blank');
                          } catch (err) {
                            logger.error('Download failed:', err);
                          }
                        }}
                        className="text-xs"
                      >
                        Download
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Auto-Saves sub-component
// ---------------------------------------------------------------------------

interface AutoSavesContentProps {
  loading: boolean;
  error: string | null;
  groups: AutoSaveGroup[];
  initialDraft: S3VersionInfo | null;
  expandedGroup: string | null;
  restoringVersionId: string | null;
  onRetry: () => void;
  onToggleGroup: (id: string) => void;
  onRestore: (versionId: string) => void;
}

function AutoSavesContent({
  loading,
  error,
  groups,
  initialDraft,
  expandedGroup,
  restoringVersionId,
  onRetry,
  onToggleGroup,
  onRestore,
}: AutoSavesContentProps) {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-32 gap-2">
        <Spinner size="sm" />
        <span className="text-sm text-muted-foreground">Loading auto-saves...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <div className="text-sm text-destructive mb-2">{error}</div>
        <Button variant="secondary" size="sm" onClick={onRetry}>
          Retry
        </Button>
      </div>
    );
  }

  if (groups.length === 0 && !initialDraft) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        <p className="text-sm">No auto-saved versions available</p>
      </div>
    );
  }

  return (
    <div className="divide-y divide-border">
      {/* Session groups */}
      {groups.map((group) => {
        const isExpanded = expandedGroup === group.id;

        return (
          <div key={group.id}>
            {/* Group header */}
            <div
              className="px-4 py-3 cursor-pointer hover:bg-muted transition-colors"
              onClick={() => onToggleGroup(group.id)}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-foreground">
                    {formatTimeRange(group.startTime, group.endTime)}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {group.versions.length} {group.versions.length === 1 ? 'save' : 'saves'}
                  </div>
                </div>
                <span className="text-muted-foreground text-xs ml-2 flex-shrink-0">
                  {isExpanded ? '▾' : '▸'}
                </span>
              </div>
            </div>

            {/* Expanded version list */}
            {isExpanded && (
              <div className="bg-muted/50 border-t border-border">
                {group.versions.map((version) => (
                  <div
                    key={version.versionId}
                    className="px-4 py-2 flex items-center justify-between border-b border-border last:border-b-0"
                  >
                    <div className="min-w-0">
                      <div className="text-xs font-medium text-foreground">
                        {formatTimestamp(new Date(version.lastModified))}
                      </div>
                      <div className="text-xs text-muted-foreground">{formatFileSize(version.size)}</div>
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => onRestore(version.versionId)}
                      disabled={restoringVersionId !== null}
                      className="text-xs ml-2 flex-shrink-0"
                    >
                      {restoringVersionId === version.versionId ? (
                        <span className="flex items-center gap-1">
                          <Spinner size="sm" />
                          Restoring…
                        </span>
                      ) : (
                        'Restore'
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Initial Draft State — pinned at bottom */}
      {initialDraft && (
        <div className="px-4 py-3 bg-muted/30">
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-foreground">Initial Draft State</span>
                <Badge variant="secondary" className="text-xs">
                  Origin
                </Badge>
              </div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {new Date(initialDraft.lastModified).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </div>
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onRestore(initialDraft.versionId)}
              disabled={restoringVersionId !== null}
              className="text-xs ml-2 flex-shrink-0"
            >
              {restoringVersionId === initialDraft.versionId ? (
                <span className="flex items-center gap-1">
                  <Spinner size="sm" />
                  Restoring…
                </span>
              ) : (
                'Restore'
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
